import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { IconInfoSmall } from '@components/icons/info-small';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';

import { SubmitError } from 'api/departments/requests';
import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

type Props = ModalProps<{
  userId: number;
  departmentId: number;
  departmentTitle: string;
  fullAccess: boolean;
  canEditFullAccess: boolean;
}>;

export const EditDepartmentUserModal = (props: Props) => {
  const { t } = useStore();
  const { closeModal, departmentTitle, departmentId, userId, canEditFullAccess } = props;
  const { showError, showSuccess } = useToaster();

  const [fullAccess, setFullAccess] = useState(props.fullAccess);

  const queryClient = useQueryClient();
  const submit = useMutation<{}, SubmitError>(
    () => api.departments.editUser(departmentId, userId, fullAccess),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['departments']);
        showSuccess(t('modals.editdepartmentuser.success'));
        closeModal();
      },
      onError: (error) => {
        showError(error.response ? error.response.data.error : error.message);
      },
    }
  );

  return (
    <Stack space={6}>
      <Text font="heading1" text={t('modals.editdepartmentuser.header')} />
      <Stack space={4}>
        <Stack space={0}>
          <Text font="semibold14" text={t('modals.adddepartmentuser.department.label')} />
          <Text font="regular16" text={departmentTitle} />
        </Stack>
        <Stack space={0}>
          <Stack space={1} orientation="horizontal" align="center">
            <Text font="semibold14" text={t('modals.adddepartmentuser.fullaccess.label')} />
            <IconInfoSmall
              color="purple50"
              tooltip={t('modals.adddepartmentuser.fullaccess.tooltip')}
            />
          </Stack>
          <Stack space={0} orientation="horizontal" justify={'space-between'}>
            <Text text={fullAccess ? t('common.yes') : t('common.no')} />
            <Switch
              onChange={setFullAccess}
              label={t('modals.adddepartmentuser.fullaccess.label')}
              labelVisible={false}
              defaultValue={fullAccess}
              isDisabled={!canEditFullAccess}
            />
          </Stack>
        </Stack>
        <Text
          color="purple80"
          font="regular14"
          text={t('modals.adddepartmentuser.fullaccess.description')}
        />
        {fullAccess && !props.fullAccess && (
          <Text
            color="red100"
            font="regular14"
            text={t('modals.adddepartmentuser.fullaccess.yousure')}
          />
        )}
      </Stack>
      <Stack orientation="horizontal" space={4} justify="flex-end">
        <Button
          variant="secondary"
          onClick={closeModal}
          text={t('modals.adddepartmentuser.cancel')}
        />
        <Button
          onClick={() => submit.mutate()}
          text={t('modals.editdepartmentuser.save')}
          variant="primary-blue"
          isDisabled={fullAccess === props.fullAccess}
        />
      </Stack>
    </Stack>
  );
};
